<template>
  <Dialog
    :visible="visibility"
    modal
    header="Search the Web"
    :style="dialogStyle"
    :closable="false"
  >
    <div class="search-row">
      <InputText 
        v-model="query" 
        placeholder="Enter keywords" 
        @keyup.enter="search" 
      />
      <Button 
        label="Search" 
        class="p-ml-2" 
        @click="search" 
      />
    </div>

    <div 
      v-if="loading" 
      class="p-mt-3"
    >
      Searching...
    </div>

    <ul 
      v-if="suggestions.length" 
      class="suggestions"
    >
      <li 
        v-for="item in suggestions" 
        :key="item.url" 
        class="suggestion-item"
      >
        <div class="suggestion-left">
          <a 
            :href="item.url" 
            target="_blank"
          >
            {{ item.title }}
          </a>
          <div class="suggestion-url">
            {{ item.url }}
          </div>
        </div>
        <div class="suggestion-right">
          <Button 
            label="Add" 
            class="p-button-sm" 
            @click="add(item)" 
          />
        </div>
      </li>
    </ul>

    <div class="dialog-footer">
      <Button
        type="button"
        class="p-close-button"
        label="Close"
        @click="closeDialog"
      />
    </div>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import {
    BASE_URL,
    LIBRARY_WEBSITE_SUGGESTION_ENDPOINT
} from "@/assets/constants/api_endpoints.js";
import { getWithAuthentication } from "@/services/api";


export default {
  components: { Dialog, InputText, Button },
  props: {
    visible: { type: Boolean, default: false },
    subjectId: { type: String, default: null },
  },
  emits: ['updateVisibility', 'add-url'],
  data() {
    return {
      query: '',
      suggestions: [],
      loading: false,
    };
  },
  computed: {
    visibility: {
      get() { return this.visible },
      set(value) { this.$emit('updateVisibility', value) }
    },
    dialogStyle() {
      if (typeof window !== 'undefined' && window.innerWidth < 768) {
        return { width: '90vw', maxWidth: '100%' };
      }
      return { width: '40rem' };
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
    methods: {
    closeDialog() {
      this.visibility = false;
    },
    handleResize() {
      this.$forceUpdate();
    },
    setQueryAndSearch(query) {
      this.query = query;
      this.search();
    },
    async getWebsiteSuggestions(query) {
    try {
      let websites = [];
      const response = await getWithAuthentication(
        BASE_URL,
        LIBRARY_WEBSITE_SUGGESTION_ENDPOINT + `?query=${query}`
      );
      if (response && response.status === 200) {
        let data = response.data;
        if (data) {
          // If backend returns a stringified JSON, parse it
          if (typeof data === 'string') {
            try {
              const parsed = JSON.parse(data);
              if (Array.isArray(parsed)) {
                websites = parsed;
              } else if (parsed && typeof parsed === 'object') {
                websites = [parsed];
              } else {
                websites = [ { title: data, url: data } ];
              }
            } catch (e) {
              // Not JSON, treat as plain string
              websites = [ { title: data, url: data } ];
            }
          } else if (Array.isArray(data)) {
            websites = data;
          } else if (data && typeof data === 'object') {
            websites = [data];
          }
        }
      }
      return websites;
    } catch (error) {
      this.$router.push({ name: "LoginForm", params: { loggedOut: true } });
    }
    },
    async search() {
      if (!this.query || !this.query.trim()) {
        this.suggestions = [];
        return;
      }
      this.loading = true;
      const q = encodeURIComponent(this.query.trim());
      this.suggestions = await this.getWebsiteSuggestions(this.query);
      setTimeout(() => { this.loading = false }, 250);
    },
    add(item) {
      this.$emit('add-url', item.url);
      this.closeDialog();
    }
  }
}
</script>

<style scoped>
.search-row {
  display:flex;
  gap:8px;
  align-items:center;
}
.suggestions {
  list-style:none;
  padding:0;
  margin-top:1rem;
  max-height:240px;
  overflow:auto;
}
.suggestion-item {
  display:flex;
  justify-content:space-between;
  align-items:center;
  padding:0.5rem 0;
  border-bottom:1px solid #eee;
}
.suggestion-url {
  font-size:0.8rem;
  color:#666;
}
.dialog-footer { margin-top:1rem; }
.p-close-button { float: right; display: inline; margin-top: 10px; }

@media (max-width: 767px) {
  .search-row {
    flex-direction: column;
    gap: 0.5rem;
  }
  .search-row :deep(input) {
    width: 100%;
  }
  .search-row :deep(.p-button) {
    width: 100%;
  }
  .suggestion-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  .suggestion-right {
    width: auto;
  }
  .suggestion-right :deep(.p-button) {
    width: auto;
  }
}
</style>
